import { z } from 'zod';
import { MEMBERSHIP_STATUSES, MEMBERSHIP_TRANSITIONS, PAUSE_STATUSES } from '@bulk/types';
import { dateOnlySchema, dateTimeSchema, idSchema, paginationQuerySchema, sortOrderSchema } from './common';

export const createMembershipSchema = z.object({
  memberId: idSchema,
  planId: idSchema,
  startsAt: dateTimeSchema.optional(),
  activateImmediately: z.boolean().default(false).describe('Gives the membership without a payment: needs memberships.comp and a reason. Without it the membership stays PENDING and opens no door'),
  reason: z.string().trim().max(500).optional().describe('Why the membership is given without a payment; required with activateImmediately'),
});
export type CreateMembershipInput = z.infer<typeof createMembershipSchema>;

export const membershipSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  memberId: idSchema,
  planId: idSchema,
  planName: z.string(),
  orderId: idSchema.nullable(),
  status: z.enum(MEMBERSHIP_STATUSES),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  pausedAt: z.string().nullable(),
  pausedDaysTotal: z.number().int(),
  pauseCount: z.number().int(),
  visitsUsed: z.number().int(),
  visitLimit: z.number().int().nullable().describe('Visits the plan allows over the whole term, or null when unlimited'),
  autoRenew: z.boolean(),
  cancelledAt: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  deletedAt: z.string().nullable().describe('Set when the membership was deleted as entered by mistake; it is then CANCELLED and listed only with deleted=true'),
  deleteReason: z.string().nullable(),
  deletedByName: z.string().nullable().describe('Who deleted it, in the deleted list'),
  paidBy: z.string().nullable().describe("The company paying for it on a corporate invoice, in the memberships list; null otherwise. A pending one waits for the company's transfer, not for the member"),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MembershipDto = z.infer<typeof membershipSchema>;

export const membershipHistorySchema = z.object({
  id: idSchema,
  membershipId: idSchema,
  transition: z.enum(MEMBERSHIP_TRANSITIONS),
  fromStatus: z.enum(MEMBERSHIP_STATUSES).nullable(),
  toStatus: z.enum(MEMBERSHIP_STATUSES),
  actorUserId: idSchema.nullable(),
  reason: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type MembershipHistoryDto = z.infer<typeof membershipHistorySchema>;

export const MEMBERSHIP_LIST_SORTS = ['createdAt', 'plan', 'status', 'startsAt', 'endsAt', 'visits'] as const;

export const membershipListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(MEMBERSHIP_STATUSES).optional(),
  memberId: idSchema.optional(),
  expiringWithinDays: z.coerce.number().int().min(1).max(365).optional(),
  deleted: z.stringbool().default(false).describe('Only the memberships deleted in the gym, with who deleted them and why (memberships.delete)'),
  sort: z.enum(MEMBERSHIP_LIST_SORTS).default('createdAt').describe('Memberships without a start or end date come last in either direction'),
  order: sortOrderSchema.default('desc'),
});
export type MembershipListQuery = z.infer<typeof membershipListQuerySchema>;

export const requestPauseSchema = z
  .object({
    startDate: dateOnlySchema,
    endDate: dateOnlySchema,
    reason: z.string().trim().max(500).optional().describe("Required when staff freeze another person's membership"),
  })
  .refine((v) => v.endDate > v.startDate, { message: 'endDate must be after startDate' });
export type RequestPauseInput = z.infer<typeof requestPauseSchema>;

export const pauseSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  membershipId: idSchema,
  startDate: z.string(),
  endDate: z.string(),
  effectiveEndDate: z.string().nullable(),
  status: z.enum(PAUSE_STATUSES),
  reason: z.string().nullable(),
  createdByUserId: idSchema.nullable(),
  approvedByUserId: idSchema.nullable(),
  createdAt: z.string(),
});
export type PauseDto = z.infer<typeof pauseSchema>;

export const deleteMembershipSchema = z.object({
  reason: z.string().trim().max(500).optional().describe('Why the membership is deleted, at least 5 characters (422 REASON_REQUIRED)'),
});

export const membershipActionSchema = z.object({
  reason: z.string().trim().max(500).optional().describe('Required to activate or renew, which give time without a payment'),
});
export type MembershipActionInput = z.infer<typeof membershipActionSchema>;
